import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTenantStats, getTenantUsers, createTenantUser, toggleUserActive } from '../../services/apiSuperAdmin';
import { useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Set locale globally for this file or just use it inline
dayjs.locale('es');

export default function TenantStatsDrawer({ isOpen, onClose, tenant }) {
    const qc = useQueryClient();
    const impersonate = useAuthStore(s => s.impersonate);
    const navigate = useNavigate();
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'agent' });
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [impersonatingId, setImpersonatingId] = useState(null);

    const handleImpersonate = async (userId) => {
        if (!window.confirm('¿Estás seguro de que quieres iniciar sesión como este usuario?')) {
            return;
        }
        setImpersonatingId(userId);
        const success = await impersonate(userId);
        setImpersonatingId(null);
        if (success) {
            onClose();
            navigate('/');
        }
    };

    const { data: stats, isLoading, error } = useQuery({
        queryKey: ['tenant-stats', tenant?.id],
        queryFn: () => getTenantStats(tenant.id),
        enabled: !!tenant && isOpen,
        staleTime: 30000,
    });

    const { data: users, isLoading: loadingUsers } = useQuery({
        queryKey: ['tenant-users', tenant?.id],
        queryFn: () => getTenantUsers(tenant.id),
        enabled: !!tenant && isOpen,
    });

    const toggleActiveMut = useMutation({
        mutationFn: toggleUserActive,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['tenant-users', tenant?.id] });
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
        }
    });

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setFormError('');
        setSubmitting(true);
        try {
            await createTenantUser(tenant.id, newUser);
            setNewUser({ name: '', email: '', password: '', role: 'agent' });
            setShowAddForm(false);
            qc.invalidateQueries({ queryKey: ['tenant-users', tenant?.id] });
            qc.invalidateQueries({ queryKey: ['super-admin-tenants'] });
        } catch (err) {
            setFormError(err.response?.data?.message || err.response?.data?.errors?.email?.[0] || 'Error al crear usuario.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
            
            <div className="absolute inset-y-0 right-0 w-full max-w-xl bg-slate-50 dark:bg-background-dark shadow-2xl flex flex-col transform transition-transform duration-300">
                {/* Header */}
                <div className="bg-white dark:bg-surface-dark border-b border-gray-100 dark:border-gray-800 px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                            <span className="material-symbols-outlined text-violet-600 dark:text-violet-400 text-[22px]">
                                monitoring
                            </span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                Estadísticas: {tenant?.name}
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                /{tenant?.slug} • {tenant?.is_active ? 'Activo' : 'Inactivo'}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="w-10 h-10 border-4 border-violet-100 border-t-violet-600 rounded-full animate-spin" />
                            <p className="text-sm text-slate-500">Cargando estadísticas...</p>
                        </div>
                    ) : error ? (
                        <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl text-center">
                            <span className="material-symbols-outlined text-red-500 text-4xl mb-2">error</span>
                            <p className="text-sm text-red-600 dark:text-red-400 font-medium">Error al cargar datos</p>
                        </div>
                    ) : (
                        <>
                            {/* Metric Cards */}
                            <div className="grid grid-cols-2 gap-4">
                                <MetricCard icon="group" label="Contactos" value={stats?.metrics?.contacts ?? 0} color="violet" />
                                <MetricCard icon="chat" label="Conversaciones" value={stats?.metrics?.conversations ?? 0} color="sky" />
                                <MetricCard icon="mail" label="Mensajes" value={stats?.metrics?.messages ?? 0} color="emerald" />
                                <MetricCard icon="payments" label="Oportunidades" value={stats?.metrics?.deals ?? 0} color="amber" />
                            </div>

                            {/* Integrations Section */}
                            <section>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">settings_input_component</span>
                                    Configuración Global e Integraciones
                                </h3>
                                <div className="space-y-3">
                                    <IntegrationRow 
                                        icon="chat_bubble" 
                                        label="WhatsApp (Evolution)" 
                                        status={stats.integrations.whatsapp.active > 0 ? 'connected' : 'disconnected'}
                                        info={`${stats.integrations.whatsapp.active} de ${stats.integrations.whatsapp.total} instancias activas`}
                                        brandColor="bg-emerald-500"
                                    />
                                    <IntegrationRow 
                                        icon="alternate_email" 
                                        label="Cuentas de Gmail / Email" 
                                        status={stats.integrations.email.active > 0 ? 'connected' : 'disconnected'}
                                        info={`${stats.integrations.email.active} cuentas vinculadas`}
                                        brandColor="bg-red-500"
                                    />
                                    <IntegrationRow 
                                        icon="camera" 
                                        label="Instagram Direct" 
                                        status={stats.integrations.instagram.active > 0 ? 'connected' : 'disconnected'}
                                        info={stats.integrations.instagram.active > 0 ? 'Conectado' : 'Sin vincular'}
                                        brandColor="bg-pink-500"
                                    />
                                    <IntegrationRow 
                                        icon="auto_awesome" 
                                        label="API de IA & Estrategia" 
                                        status={stats.integrations.ai.active ? 'connected' : 'disconnected'}
                                        info={stats.integrations.ai.strategy}
                                        brandColor="bg-violet-500"
                                    />
                                    <IntegrationRow 
                                        icon="api" 
                                        label="Webhooks & n8n" 
                                        status={stats.integrations.webhooks.active > 0 ? 'connected' : 'disconnected'}
                                        info={`${stats.integrations.webhooks.active} configuraciones activas`}
                                        brandColor="bg-blue-500"
                                    />
                                </div>
                            </section>

                            {/* Users & Team Section */}
                            <section className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px] text-violet-500">group</span>
                                        Usuarios y Equipo del Cliente
                                    </h3>
                                    {!showAddForm && (
                                        <button
                                            onClick={() => setShowAddForm(true)}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-all"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">person_add</span>
                                            Agregar Usuario
                                        </button>
                                    )}
                                </div>

                                {showAddForm && (
                                    <form onSubmit={handleCreateUser} className="bg-white dark:bg-surface-dark border border-slate-200 dark:border-white/10 rounded-2xl p-5 space-y-4 animate-in slide-in-from-top-2 duration-200 shadow-sm">
                                        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[16px]">person_add</span>
                                            Nuevo Colaborador
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="col-span-2">
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Nombre Completo</label>
                                                <input
                                                    type="text"
                                                    required
                                                    placeholder="Ej. Juan Pérez"
                                                    value={newUser.name}
                                                    onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                                                    className="w-full text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    required
                                                    placeholder="juan@empresa.com"
                                                    value={newUser.email}
                                                    onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                                                    className="w-full text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Contraseña</label>
                                                <input
                                                    type="password"
                                                    required
                                                    placeholder="Mín. 8 caracteres"
                                                    value={newUser.password}
                                                    onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                                                    className="w-full text-xs bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Perfil (Rol)</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewUser({ ...newUser, role: 'manager' })}
                                                        className={`py-2 px-3 rounded-lg border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                                            newUser.role === 'manager'
                                                            ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                                                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10'
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">manage_accounts</span>
                                                        Manager
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setNewUser({ ...newUser, role: 'agent' })}
                                                        className={`py-2 px-3 rounded-lg border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                                                            newUser.role === 'agent'
                                                            ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm'
                                                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200 dark:bg-white/5 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/10'
                                                        }`}
                                                    >
                                                        <span className="material-symbols-outlined text-[14px]">support_agent</span>
                                                        Vendedor
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {formError && (
                                            <p className="text-[11px] text-red-500 font-semibold bg-red-50 dark:bg-red-950/20 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/30">{formError}</p>
                                        )}

                                        <div className="flex justify-end gap-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => { setShowAddForm(false); setFormError(''); }}
                                                className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors font-medium"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="px-4 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-400 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5"
                                            >
                                                {submitting && <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />}
                                                Guardar Colaborador
                                            </button>
                                        </div>
                                    </form>
                                )}

                                <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                                    {loadingUsers ? (
                                        <div className="flex items-center justify-center p-8">
                                            <div className="w-6 h-6 border-2 border-violet-100 border-t-violet-600 rounded-full animate-spin" />
                                        </div>
                                    ) : (!users || users.length === 0) ? (
                                        <div className="p-8 text-center text-slate-400 text-xs">
                                            No hay usuarios en este tenant
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-50 dark:divide-gray-800">
                                            {users.map(u => (
                                                <div key={u.id} className="flex items-center justify-between p-4 hover:bg-slate-50/55 dark:hover:bg-white/5 transition-colors">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-bold flex items-center justify-center text-slate-600 dark:text-slate-300">
                                                            {u.name.split(' ').map(n => n[0]).slice(0,2).join('').toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{u.name}</span>
                                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                                                    u.role === 'manager' 
                                                                    ? 'bg-blue-50 text-blue-600 border border-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800' 
                                                                    : 'bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
                                                                }`}>
                                                                    {u.role === 'manager' ? 'Manager' : 'Vendedor'}
                                                                </span>
                                                            </div>
                                                            <span className="text-[10px] text-slate-400 block mt-0.5">{u.email}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            title="Iniciar sesión como este usuario"
                                                            onClick={() => handleImpersonate(u.id)}
                                                            disabled={!u.is_active || impersonatingId !== null}
                                                            className={`p-1.5 rounded-lg border transition-all flex items-center justify-center ${
                                                                u.is_active
                                                                ? 'bg-violet-50 text-violet-600 border-violet-100 hover:bg-violet-600 hover:text-white dark:bg-violet-950/20 dark:text-violet-400 dark:border-violet-900/40 dark:hover:bg-violet-600 dark:hover:text-white'
                                                                : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed dark:bg-slate-800 dark:text-slate-600 dark:border-slate-700'
                                                            }`}
                                                        >
                                                            {impersonatingId === u.id ? (
                                                                <span className="w-[18px] h-[18px] border-2 border-current/30 border-t-current rounded-full animate-spin" />
                                                            ) : (
                                                                <span className="material-symbols-outlined text-[18px]">vpn_key</span>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => toggleActiveMut.mutate(u.id)}
                                                            disabled={toggleActiveMut.isPending}
                                                            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                                                                u.is_active
                                                                ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-red-50 hover:text-red-600 hover:border-red-100 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/40 dark:hover:bg-red-950/20 dark:hover:text-red-400 dark:hover:border-red-900/40'
                                                                : 'bg-slate-50 text-slate-400 border-slate-100 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-100 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700 dark:hover:bg-emerald-950/20 dark:hover:text-emerald-400 dark:hover:border-emerald-900/40'
                                                            }`}
                                                        >
                                                            {u.is_active ? 'Activo' : 'Inactivo'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Login History */}
                            <section>
                                <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[18px] text-violet-500">history</span>
                                    Historial Reciente de Logins
                                </h3>
                                <div className="bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                                    {(!stats?.login_history || stats.login_history.length === 0) ? (
                                        <div className="p-10 text-center">
                                            <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                                                <span className="material-symbols-outlined text-slate-300">person_off</span>
                                            </div>
                                            <p className="text-xs text-slate-400">No hay registros de login aún</p>
                                        </div>
                                    ) : (
                                        <table className="w-full text-left text-[11px]">
                                            <thead>
                                                <tr className="bg-slate-50/50 dark:bg-slate-800/30 text-slate-400 font-bold uppercase tracking-wider border-b border-gray-50 dark:border-gray-800">
                                                    <th className="px-5 py-3">Usuario</th>
                                                    <th className="px-5 py-3">Detalles de Conexión</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                                {(stats.login_history || []).map(log => (
                                                    <tr key={log.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/20 transition-colors">
                                                        <td className="px-5 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-[10px] font-bold text-violet-600">
                                                                    {log.user?.name[0]}
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold text-slate-700 dark:text-slate-200">{log.user?.name}</p>
                                                                    <p className="text-[10px] text-slate-400">{log.user?.email}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-4">
                                                            <div className="flex items-center gap-2">
                                                                <span className="material-symbols-outlined text-[14px] text-slate-300">lan</span>
                                                                <span className="text-slate-500 font-mono text-[10px]">{log.ip_address}</span>
                                                            </div>
                                                            <p className="text-[10px] text-slate-400 mt-0.5 font-medium">
                                                                {dayjs(log.created_at).format("DD/MM/YYYY · HH:mm")}
                                                            </p>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </section>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-white dark:bg-surface-dark border-t border-gray-100 dark:border-gray-800 p-6">
                    <button 
                        onClick={onClose}
                        className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 dark:bg-violet-600 dark:hover:bg-violet-700 text-white font-bold rounded-2xl transition-all shadow-lg shadow-slate-200 dark:shadow-violet-900/20 text-sm"
                    >
                        Entendido, cerrar
                    </button>
                </div>
            </div>
        </div>
    );
}

function MetricCard({ icon, label, value, color }) {
    const colors = {
        violet:  'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400',
        sky:     'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400',
        emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400',
        amber:   'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    };
    return (
        <div className="bg-white dark:bg-surface-dark p-5 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${colors[color]}`}>
                <span className="material-symbols-outlined text-[20px]">{icon}</span>
            </div>
            <p className="text-3xl font-black text-slate-900 dark:text-slate-100 tracking-tight">{value.toLocaleString()}</p>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-1">{label}</p>
        </div>
    );
}

function IntegrationRow({ icon, label, status, info, brandColor }) {
    const isConnected = status === 'connected';
    return (
        <div className="group flex items-center justify-between p-4 bg-white dark:bg-surface-dark rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:border-violet-200 dark:hover:border-violet-900/50 transition-all">
            <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 ${isConnected ? `${brandColor} text-white shadow-lg shadow-${brandColor.split('-')[1]}-500/20` : 'bg-slate-50 text-slate-400 dark:bg-slate-800'}`}>
                    <span className="material-symbols-outlined text-[24px]">{icon}</span>
                </div>
                <div>
                    <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100">{label}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{info}</p>
                </div>
            </div>
            <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-colors ${isConnected ? 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800' : 'bg-slate-50 text-slate-400 border-slate-100 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700'}`}>
                {isConnected ? 'Activo' : 'Inactivo'}
            </div>
        </div>
    );
}
